/**
 * PollCard — voter-side card for a poll INSTANCE.
 *
 * Presentation-only: no API calls inside. Caller supplies `onVote`.
 * Rendered by the CommunityPage feed (Task 16); the home's poll banner that
 * also used it retired on 2026-09-18 (the home says "TRF is asking you N
 * questions" and sends the family here — guideline §2.9).
 *
 * PR-6 (poll pipeline v2) widened "a poll" from one question to an instance
 * minted from a published FormDefinition: many questions, the authored
 * `section_label` rendered as section headers (TRF's structure survives the
 * mint instead of silently flattening), the date/number/checkbox types,
 * per-question `required`, and D-FB-14(b) `staff_only` results.
 *
 * The one-question case is a REGRESSION SURFACE, not a special case: it takes
 * the same code path and must render exactly as it did in v1 — prompt
 * paragraph, inline control, a "Vote" button gated by the disabled attribute.
 * A multi-question instance instead validates on submit, because a disabled
 * button across four questions cannot say which one is holding it.
 */
import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { MONO_LABEL_TYPE } from '@/components/editorial';
import { Lock } from 'lucide-react';
import type { PollFeedItem, PollQuestion, VoteAnswer, VoterVisibleResult } from '@/types/polls';
import PollQuestionInput, {
  emptyDraft, draftToAnswer, validateDraft, NEEDS_ANSWER, type AnswerDraft,
} from './PollQuestionInput';
import { localizeQuestion, pollLanguageState, pollStringKey } from './pollLocalization';
import { useLocale } from '@/i18n/useLocale';
import { useTranslation } from 'react-i18next';
import { resolveMsg } from '@/i18n/msg';
import { formatPreset } from '@/i18n/intl';
// #2109 R2: the form renderer's language lines (lane L3), reused so a minted
// send and an application say the same thing in the same words.
import { FormLanguageNotice, ShownInEnglish } from '@/components/apply/FormLanguageNotice';

export interface PollCardProps {
  item: PollFeedItem;
  onVote: (answers: VoteAnswer[]) => void | Promise<void>;
}

/** Only these three types have an aggregate a voter may ever see. */
const AGGREGATE_TYPES = ['single_choice', 'multi_select', 'rating'] as const;

/**
 * Consecutive questions sharing a `section_label` become one run — the mint
 * preserves authored order, so the headers group rather than repeat. A run
 * with a null label renders its questions with no header (quick sends).
 */
function sectionRuns(questions: PollQuestion[]) {
  const runs: Array<{ label: string | null; questions: PollQuestion[] }> = [];
  for (const q of questions) {
    const label = q.section_label?.trim() || null;
    const last = runs[runs.length - 1];
    if (last && last.label === label) last.questions.push(q);
    else runs.push({ label, questions: [q] });
  }
  return runs;
}

export default function PollCard({ item, onVote }: PollCardProps) {
  const { poll } = item;
  const [voted, setVoted] = useState(item.voted);

  // #2109 R2 (lane L6): every string the card shows — prompts, section
  // headers, choice labels, the post-vote bars and the summary error — reads
  // the localized questions. Ids never change, so drafts and votes are
  // language-blind.
  const { locale } = useLocale();
  const { t } = useTranslation('community');
  const rawQuestions = poll.questions;
  const language = useMemo(() => pollLanguageState(rawQuestions ?? [], locale), [rawQuestions, locale]);
  const questions: PollQuestion[] = useMemo(
    () => (rawQuestions ?? []).map((q) => localizeQuestion(q, locale)),
    [rawQuestions, locale],
  );
  const isMulti = questions.length > 1;
  const runs = sectionRuns(questions);
  // #2133: the strings still in English on a card in the reader's language.
  const englishKeys = useMemo(() => new Set(language.staleFallbacks), [language]);
  const mark = (key: string) => (englishKeys.has(key) ? <ShownInEnglish /> : null);
  const sectionMark = (run: { questions: PollQuestion[] }) => mark(pollStringKey.section(run.questions[0]._id));

  // ── D-FB-14(b): staff_only withholds results from the family, full stop ──
  // Gated here on the VALUE, not on whether the server happened to send an
  // aggregate: a family must see no counts, no bars, and no promise of them
  // even if a payload arrives mis-shaped. The server enforces the same rule.
  const resultsWithheld = poll.results_visibility === 'staff_only';
  const visibleResults = resultsWithheld ? null : item.voter_visible_results;

  // ── Local answer state: one draft per question ──
  const [drafts, setDrafts] = useState<Record<string, AnswerDraft>>(
    () => Object.fromEntries(questions.map((q) => [q._id, emptyDraft()])),
  );
  const [attempted, setAttempted] = useState(false);
  // A save in flight, and a save that failed. `saveError` is about the
  // NETWORK, not the answers — it is the one thing `summaryError` below
  // cannot express, because the answers are all perfectly valid.
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const draftFor = (q: PollQuestion) => drafts[q._id] ?? emptyDraft();
  const errors = questions.map((q) => validateDraft(q, draftFor(q)));
  const errorText = (i: number) => (errors[i] ? resolveMsg(t, errors[i]) : null);
  const firstInvalid = errors.findIndex((e) => e !== null);
  const answers = questions
    .map((q) => draftToAnswer(q, draftFor(q)))
    .filter((a): a is VoteAnswer => a !== null);

  const allValid = firstInvalid === -1;
  const canSubmit = allValid && answers.length > 0;

  // Shown only after a submit attempt, and only on the multi-question path —
  // the single-question button is disabled instead, exactly as in v1.
  const summaryError = (() => {
    if (!attempted || canSubmit) return null;
    if (!allValid) {
      const q = questions[firstInvalid];
      return errors[firstInvalid]?.key === NEEDS_ANSWER.key
        ? t('poll.error.answerQuestion', { prompt: q.prompt })
        : t('poll.error.checkQuestion', { prompt: q.prompt });
    }
    return t('poll.error.answerOne');
  })();

  async function handleSubmit() {
    setAttempted(true);
    if (!canSubmit) {
      if (firstInvalid >= 0) {
        document.getElementById(`poll-${poll._id}-q-${questions[firstInvalid]._id}`)?.focus();
      }
      return;
    }
    // The card flips only once the save has actually landed. It used to flip
    // first and `void` the promise, so a failed vote still showed the
    // post-vote face — and under PR-6's copy thanked the family for answers
    // that were never stored. Both callers hang their refetch off this same
    // promise, so nothing re-ran either and the banner just came back later
    // with no explanation.
    setSaveError(null);
    setSubmitting(true);
    try {
      await onVote(answers);
      setVoted(true);
    } catch {
      // The drafts are deliberately left untouched: a retry must not mean
      // re-answering. v1 lost one answer this way; a minted check-in would
      // lose the whole instance.
      setSaveError(t('poll.error.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  // ── Post-vote result lookup ──
  const resultFor = (q: PollQuestion): VoterVisibleResult | undefined =>
    visibleResults?.find((r) => r.question_id === q._id);
  const hasBars = (q: PollQuestion) => {
    const r = resultFor(q);
    return r !== undefined && (AGGREGATE_TYPES as readonly string[]).includes(r.type);
  };

  const anyBars = questions.some(hasBars);
  // free_text is content and the PR-6 typed answers are too attributable to
  // aggregate — a card made only of those never shows totals to anyone.
  const noAggregateAtAll = questions.length > 0
    && questions.every((q) => !(AGGREGATE_TYPES as readonly string[]).includes(q.type));

  const closingNote = (() => {
    if (!voted) return null;
    if (resultsWithheld) return t('poll.closing.withheld');
    if (noAggregateAtAll) return t('poll.closing.shared');
    if (!anyBars && poll.results_visibility === 'after_close' && poll.status === 'open') {
      // The end date follows the UI language, not the browser's (R3-B5).
      return poll.end_at
        ? t('poll.closing.afterCloseOn', { date: formatPreset(poll.end_at, 'numeric') })
        : t('poll.closing.afterClose');
    }
    return null;
  })();

  return (
    <article className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Eyebrow */}
      <header className="flex items-center gap-2 px-5 pt-4 pb-2">
        <div className={cn(MONO_LABEL_TYPE, 'text-stevens-ink-3')}>
          {t('poll.eyebrow')}
        </div>
        {poll.anonymous && (
          <span
 className={cn(MONO_LABEL_TYPE, 'flex items-center gap-1 text-stevens-ink-3')}
            aria-label={t('poll.anonymousLabel')}
          >
            <Lock className="h-3 w-3" />
            <span>{t('poll.anonymousNote')}</span>
          </span>
        )}
      </header>

      <div className="px-5 pb-2">
        <h3 className="text-card-title font-semibold leading-snug mb-3">{poll.title}</h3>
        {poll.description && (
          <p className="text-body text-stevens-ink-3 mb-3">{poll.description}</p>
        )}
        {/* #2109 R2-B1/B17: English shown to a non-English reader says so;
            R2-B18: one line when any string is a machine translation. */}
        <FormLanguageNotice localization={language} className="mb-3 space-y-1" />

        {/* ---- Pre-vote: every question, in authored sections, one submit ---- */}
        {!voted && questions.length > 0 && (
          <div className="space-y-2">
            <div className={isMulti ? 'space-y-6' : 'space-y-2'}>
              {runs.map((run, ri) => (
                <div key={`run-${ri}`} className="space-y-4">
                  {run.label && (
                    <h4 className={cn(MONO_LABEL_TYPE, 'text-stevens-ink-3')}>
                      {run.label}
                      {sectionMark(run)}
                    </h4>
                  )}
                  {run.questions.map((q) => (
                    <PollQuestionInput
                      key={q._id}
                      poll_id={poll._id}
                      question={q}
                      draft={draftFor(q)}
                      onChange={(next) => setDrafts((prev) => ({ ...prev, [q._id]: next }))}
                      error={attempted ? errorText(questions.indexOf(q)) : null}
                      englishKeys={englishKeys}
                    />
                  ))}
                </div>
              ))}
            </div>

            {summaryError && (
              <p role="alert" className="mt-2 text-body text-status-danger-fg">{summaryError}</p>
            )}

            {saveError && (
              <p role="alert" data-testid="poll-save-error" className="mt-2 text-body text-status-danger-fg">
                {saveError}
              </p>
            )}

            <button
              type="button"
              // v1 parity on the one-question path: the button itself is the
              // gate. Multi-question stays enabled so the click can SAY what is
              // missing instead of dead-ending (WCAG 3.3.1). Either way a save
              // in flight disables it — a second click would double-post.
              disabled={(isMulti ? false : !canSubmit) || submitting || undefined}
              onClick={() => { void handleSubmit(); }}
              className="mt-3 rounded-md bg-stevens-indigo-solid px-4 py-1.5 text-body font-medium text-stevens-indigo-solid-fg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-stevens-indigo-solid transition-colors"
            >
              {isMulti ? t('poll.submit') : t('poll.vote')}
            </button>
          </div>
        )}

        {/* ---- Post-vote: results only where the family is allowed them ---- */}
        {voted && (
          <div className="space-y-4" data-testid="poll-voted">
            {runs.map((run, ri) => (
              <div key={`run-${ri}`} className="space-y-4">
                {run.label && (
                  <h4 className={cn(MONO_LABEL_TYPE, 'text-stevens-ink-3')}>
                    {run.label}
                    {sectionMark(run)}
                  </h4>
                )}
                {run.questions.map((q) => {
                  const qResult = resultFor(q);
                  return (
                    <div key={q._id}>
                      <p className="text-body font-medium mb-3">{q.prompt}{mark(pollStringKey.prompt(q._id))}</p>

                      {/* Bar chart for choice / multi_select results */}
                      {hasBars(q) && (qResult!.type === 'single_choice' || qResult!.type === 'multi_select') && q.choices && (
                        <div className="space-y-2" data-testid="poll-results">
                          {q.choices.map((c) => {
                            const result = qResult as { question_id: string; type: 'single_choice' | 'multi_select'; counts: Record<string, number> };
                            const count = result.counts[c._id] ?? 0;
                            const total = Object.values(result.counts).reduce((a, b) => a + b, 0);
                            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                            return (
                              <div key={c._id}>
                                <div className="flex items-center justify-between mb-0.5 text-body">
                                  <span>{c.label}{mark(pollStringKey.choice(q._id, c._id))}</span>
                                  <span className="font-mono text-caption">{count}</span>
                                </div>
                                <div className="h-2 rounded-full bg-muted overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-stevens-indigo"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Histogram for rating results */}
                      {hasBars(q) && qResult!.type === 'rating' && (
                        <div className="flex items-end gap-1 h-16" data-testid="poll-results">
                          {([1, 2, 3, 4, 5] as const).map((n) => {
                            const result = qResult as { question_id: string; type: 'rating'; histogram: Record<1|2|3|4|5, number> };
                            const count = result.histogram[n] ?? 0;
                            const max = Math.max(...Object.values(result.histogram), 1);
                            const heightPct = Math.round((count / max) * 100);
                            return (
                              <div key={n} className="flex flex-col items-center flex-1 gap-0.5">
                                <span className="text-caption font-mono text-stevens-ink-3">{count}</span>
                                <div className="w-full rounded-t bg-muted overflow-hidden" style={{ height: '40px' }}>
                                  <div
                                    className="w-full rounded-t bg-stevens-indigo"
                                    style={{ height: `${heightPct}%`, marginTop: `${100 - heightPct}%` }}
                                  />
                                </div>
                                <span className="text-caption text-stevens-ink-3">{n}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {closingNote && <p className="text-body text-stevens-ink-3">{closingNote}</p>}
          </div>
        )}
      </div>
    </article>
  );
}
